/**
 * Telar Story – Deep Linking
 *
 * This module manages URL fragment deep linking for the story page. URL
 * fragments encode the user's current navigation position — which step they
 * are on and which panel layer (if any) is open — so that a URL can be
 * copied and shared to send a recipient directly to a specific point in the
 * story.
 *
 * Fragment format:
 *   #s{step}             — step only (e.g. #s3 = third content step)
 *   #s{step}l{layer}     — step + open panel layer (e.g. #s3l1)
 *   #s{step}l{layer}g{n} — step + layer + nth glossary link open
 *
 * Step numbers are 1-based in the fragment. state.currentIndex is 0-based.
 * state.steps[index].dataset.step is the CSV step number used by openPanel().
 *
 * All hash writes use history.replaceState exclusively — no pushState calls
 * anywhere. This means back/forward navigation exits the story
 * to the previous page rather than navigating between steps. scrollRestoration
 * is already set to 'manual' in scroll-engine.js, which prevents browser
 * scroll restoration from interfering.
 *
 * @version v1.6.0
 */

import { state } from './state.js';
import { activateCard } from './card-pool.js';
import { goToStep } from './navigation.js';
import { openPanel } from './panels.js';

// ── Deep-link panel-open timer ladder ───────────────────────────────────────────

/**
 * Pending panel-open timers scheduled by applyDeepLinkOnLoad. The panel sequence
 * runs as a short setTimeout ladder so the card stack can render before Bootstrap
 * Offcanvas opens; if the reader starts navigating during that window, the
 * deferred opens would pop panels over a now-different step. We track the timer
 * IDs so a genuine user interaction can cancel the whole ladder.
 */
let _deepLinkTimers = [];

/** Clear any pending deep-link panel-open timers. Safe to call when empty. */
function _cancelDeepLinkTimers() {
  _deepLinkTimers.forEach(clearTimeout);
  _deepLinkTimers = [];
}

/**
 * Cancel the deep-link timer ladder on the first genuine user interaction.
 * Listens for wheel / keydown / touchstart — all user-initiated. We deliberately
 * do NOT listen for the Lenis 'scroll' event: applyDeepLinkOnLoad's own
 * immediate jump emits a 'scroll', which would self-cancel the ladder before any
 * panel opened. The handler clears the timers and removes all three listeners.
 * Must be armed AFTER the jump's scrollTo so it can't be tripped by the jump.
 */
function _armDeepLinkCancellation() {
  const cancel = () => {
    _cancelDeepLinkTimers();
    window.removeEventListener('wheel', cancel);
    window.removeEventListener('keydown', cancel);
    window.removeEventListener('touchstart', cancel);
  };
  window.addEventListener('wheel', cancel, { passive: true });
  window.addEventListener('keydown', cancel);
  window.addEventListener('touchstart', cancel, { passive: true });
}

// ── Fragment regex ────────────────────────────────────────────────────────────

/**
 * Matches all defined fragment formats:
 *   #s{step}
 *   #s{step}l{layer}
 *   #s{step}l{layer}g{n}
 */
const FRAGMENT_RE = /^#s(\d+)(?:l(\d+)(?:(g)(\d+))?)?$/;

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Parse a URL fragment string into a structured navigation target.
 *
 * Returns null for empty, missing, or malformed hashes — null means the
 * intro state (no step is targeted). No hash = intro.
 *
 * @param {string} hash - The URL fragment string (e.g. '#s3l1').
 * @returns {{ step: number, layer: number|null, subType: string|null, subN: number|null }|null}
 */
export function parseFragment(hash) {
  if (!hash || hash === '#') return null;
  const m = FRAGMENT_RE.exec(hash);
  if (!m) return null;
  return {
    step: parseInt(m[1], 10),              // 1-based step number
    layer: m[2] ? parseInt(m[2], 10) : null,
    subType: m[3] || null,                 // 'g' or null
    subN: m[4] ? parseInt(m[4], 10) : null,
  };
}

/**
 * Build fragment from current state and write via replaceState.
 *
 * Reads state.currentIndex (0-based). If < 0, removes the fragment entirely
 * (intro state). Otherwise builds #s{N} (1-based), and appends l{layer} if
 * a numbered layer panel is open at the top of the panel stack.
 *
 * Glossary panels are not reflected in the layer segment — they overlay any
 * layer and do not receive their own l{} token. writeHashWithGlossary()
 * handles writing the g{n} sub-token when a glossary link is activated.
 */
export function writeHash() {
  _writeHashFragment(null);
}

/**
 * Write fragment with a glossary sub-link token (g{n} format).
 *
 * Reads current step and panel layer from state, then appends g{n} to encode
 * which glossary link within the panel was activated. The integer n is taken
 * directly from the data-deep-link-n attribute assigned at panel render time
 * (parseInt validates it as a number before this call).
 *
 * @param {number} n - 1-based running number of the clicked glossary link.
 */
export function writeHashWithGlossary(n) {
  _writeHashFragment(n);
}

/**
 * Internal helper — builds and writes the fragment.
 *
 * @param {number|null} glossaryN - 1-based glossary sub-link running number, or null.
 */
function _writeHashFragment(glossaryN) {
  const idx = state.currentIndex;

  let hash = '';

  if (idx >= 0) {
    hash = `#s${idx + 1}`; // 1-based step number

    if (state.panelStack.length > 0) {
      // Find the topmost numbered layer panel (ignores 'glossary' entries which
      // are not numbered layers and do not get their own l{} token).
      for (let i = state.panelStack.length - 1; i >= 0; i--) {
        const layerMatch = state.panelStack[i].type.match(/^layer(\d+)$/);
        if (layerMatch) {
          hash += `l${layerMatch[1]}`;
          // Append glossary sub-link token if provided (g{n})
          if (glossaryN !== null) {
            hash += `g${glossaryN}`;
          }
          break;
        }
      }
    }
  }

  if (hash) {
    history.replaceState(null, '', hash);
  } else {
    // Intro state — remove fragment entirely
    history.replaceState(null, '', window.location.pathname + window.location.search);
  }
}

/**
 * Navigate back to the intro / title card from within the story.
 *
 * Scrolls to position 0 (or activates intro in button mode), restores the
 * intro card via goToStep(-1), hides all viewer plates, and clears the hash.
 */
export function navigateToIntro() {
  // Hide all active viewer plates
  for (const plate of Object.values(state.viewerPlates)) {
    plate.classList.remove('is-active');
  }

  if (state.lenis) {
    state.lenis.stop();
    document.documentElement.scrollTop = 0;
    state.lenis.animatedScroll = 0;
    state.lenis.targetScroll = 0;
    state.currentIndex = -1;
    state.scrollPosition = 0;
    requestAnimationFrame(() => { state.lenis.start(); });
  } else {
    state.currentMobileStep = -1;
    state.mobileInIntro = true;
    state.steps.forEach(step => step.classList.remove('mobile-active'));
  }

  // Use the navigation module to restore intro card visuals
  goToStep(-1, 'backward');
  writeHash();
}

/**
 * Navigate to a specific step from within the story (e.g. TOC links).
 *
 * Unlike applyDeepLinkOnLoad (which runs once at page load), this can be
 * called at any time during the story. It jumps the scroll position and
 * activates the target card, then updates the URL hash.
 *
 * @param {number} stepNumber - 1-based step number (matches CSV step column).
 */
export function navigateToStep(stepNumber) {
  const targetIndex = stepNumber - 1;
  if (targetIndex < 0 || targetIndex >= state.steps.length) return;

  // Hide all active viewer plates before jumping — prevents plates from
  // nearby steps bleeding through when the target is a title/section card.
  for (const plate of Object.values(state.viewerPlates)) {
    plate.classList.remove('is-active');
  }

  if (state.lenis) {
    const targetPx = (targetIndex + 1) * window.innerHeight;

    // scrollTo must jump straight to the target with no animation. An animated
    // scroll drives the per-frame IIIF interpolation (lerpIiifPosition) at each
    // intermediate frame; lerpIiifPosition only skips interpolating once
    // progress is within 0.001 of the integer step, so an animated approach
    // can leave the viewer on the last interpolated (not authored) x/y/zoom
    // instead of landing exactly on the target position. immediate:true removes
    // the intermediate frames entirely; force:true overrides the Snap plugin's
    // lock/stopped state so the jump isn't blocked.
    state.lenis.scrollTo(targetPx, { immediate: true, force: true });
    if (state.snap) state.snap.currentSnapIndex = targetIndex + 1; // keep Snap aligned (matches keyboardNav)

    activateCard(targetIndex, 'forward');
    state.currentIndex = targetIndex;
    state.scrollPosition = targetIndex + 1;
  } else {
    state.currentMobileStep = targetIndex;
    state.mobileInIntro = false;
    activateCard(targetIndex, 'forward');

    state.steps.forEach((step, i) => {
      if (i === targetIndex) {
        step.classList.add('mobile-active');
      } else {
        step.classList.remove('mobile-active');
      }
    });
  }

  writeHash();
}

/**
 * Read the URL fragment on page load and jump to the encoded position.
 *
 * Must be called after initCardPool() and after the navigation mode is
 * initialised (initScrollEngine or initializeButtonNavigation), and after
 * initializePanels() — because it may call openPanel() which requires panels
 * to be ready.
 *
 * Instant jump: uses duration: 0 for no scroll animation on load.
 * Panel applied after step position: step first, then panel via
 * setTimeout to let the card stack render before Bootstrap Offcanvas opens.
 *
 * Sub-panel links (g{n}):
 *   Glossary sub-links (g{n}) are activated here: after the panel opens,
 *   the glossary link with matching data-deep-link-n is clicked. Best-effort.
 */
export function applyDeepLinkOnLoad() {
  const parsed = parseFragment(window.location.hash);
  if (!parsed) return; // No fragment = intro — nothing to do

  // Clamp to valid step range
  const targetIndex = Math.min(parsed.step - 1, state.steps.length - 1);
  if (targetIndex < 0) return;

  if (state.lenis) {
    // Desktop Lenis mode: instant scroll jump to the correct viewport position.
    // Position model: intro = 0, step 0 = 1 * innerHeight, step 1 = 2 * innerHeight …
    //
    // scrollTo must jump straight to the target with no animation — see
    // navigateToStep for why (an animated scroll can leave the per-frame IIIF
    // lerp on an interpolated position instead of the authored one).
    const targetPx = (targetIndex + 1) * window.innerHeight;
    state.lenis.scrollTo(targetPx, { immediate: true, force: true });
    if (state.snap) state.snap.currentSnapIndex = targetIndex + 1; // keep Snap aligned

    // Activate card and sync state
    activateCard(targetIndex, 'forward');
    state.currentIndex = targetIndex;
    state.scrollPosition = targetIndex + 1;
  } else {
    // Button/mobile/iOS mode: no scroll surface — activate card directly
    state.currentMobileStep = targetIndex;
    state.mobileInIntro = false;
    activateCard(targetIndex, 'forward');

    // Ensure the correct step has the mobile-active class
    state.steps.forEach((step, i) => {
      if (i === targetIndex) {
        step.classList.add('mobile-active');
      } else {
        step.classList.remove('mobile-active');
      }
    });
  }

  // Panel state: apply after step position, with delay for card render.
  // Open parent layers underneath the target: layer2 needs layer1 open first,
  // and glossary sub-links need their parent layer open underneath.
  if (parsed.layer !== null) {
    const stepNumber = state.steps[targetIndex]?.dataset?.step;
    if (stepNumber) {
      let delay = 100;

      // Each deferred open also re-checks that we are still on the deep-link
      // target before acting — a backstop that covers navigation paths the
      // interaction listener can't (e.g. a mobile nav-button tap), and the lenis
      // vs button mode use different position fields.
      const onTarget = () => state.lenis
        ? state.currentIndex === targetIndex
        : state.currentMobileStep === targetIndex;

      // Open layer1 first if the target is layer2 or deeper
      if (parsed.layer >= 2) {
        _deepLinkTimers.push(setTimeout(() => {
          if (onTarget()) openPanel('layer1', stepNumber);
        }, delay));
        delay += 200;
      }

      // Open the target layer
      _deepLinkTimers.push(setTimeout(() => {
        if (onTarget()) openPanel('layer' + parsed.layer, stepNumber);
      }, delay));
      delay += 200;

      // Glossary sub-link activation: after the panel opens, find the
      // glossary link with the matching running number and click it to open
      // the glossary entry. Best-effort — if the panel content hasn't loaded
      // in time the click target won't exist and the sub-link is silently
      // skipped.
      if (parsed.subType === 'g' && parsed.subN !== null) {
        _deepLinkTimers.push(setTimeout(() => {
          if (!onTarget()) return;
          const panelContent = document.getElementById('panel-layer' + parsed.layer + '-content');
          if (panelContent) {
            const target = panelContent.querySelector(`[data-deep-link-n="${parsed.subN}"]`);
            if (target) target.click();
          }
        }, delay));
      }

      // Arm cancellation only now that timers are scheduled — and after the
      // jump above, so the jump's own scroll can't trip it.
      if (_deepLinkTimers.length) _armDeepLinkCancellation();
    }
  }
}
